import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js'
import { format, parseISO, sub, subDays } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Line } from 'react-chartjs-2'

import '../../../assets/styles/checkbox.css'
import NoIndexGraph from './no-index-graph'
import TabButton from './tab-button'
import {
  useGetHistoryIndexSitesQuery,
  useLazyGetHistoryIndexChartQuery,
} from '../../../store/api'
import type { HistoricalIndexRequest } from '../../../store/types/dashboard'
import type { ProductSite } from '../../../store/types/product'
import DateRangePicker from '../../../utils/date-pickers/range-picker'
import ManageTableCheckbox from '../../../utils/forms/checkbox/manage-table-checkbox'
import SpinLoader from '../../../utils/loaders/spin'
import Mixpanel from '../../../utils/mixpanel/Mixpanel'
import { LeftArrow, RightArrow } from '../../icons/general'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  Filler,
)

type ChartData = {
  data?: {
    chart: Array<any>
    sites: Array<any>
  }
}

export default function HistoricalIndexChart({ data }: ChartData) {
  const { data: sites, isSuccess: isSitesLoaded } =
    useGetHistoryIndexSitesQuery({})
  const [
    getChartData,
    {
      data: indexChartData,
      currentData: currentIndexChartData,
      isLoading: isChartDataLoading,
      isSuccess: isChartDataSuccess,
    },
  ] = useLazyGetHistoryIndexChartQuery()
  const lineRef = useRef<ChartJS<'line', number[], string>>(null)
  const [chartLegends, setChartLegends] = useState<Array<Object>>()
  const [paginations, setPaginations] = useState({ first: 0, last: 10 })
  const [isVisible, setIsVisible] = useState<Array<number>>([])

  const [filter, setFilter] = useState<
    HistoricalIndexRequest & { key: string }
  >({
    key: '7days',
    startAt: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endAt: format(new Date(), 'yyyy-MM-dd'),
  })

  const { key, ...dates } = filter

  const [setSites, updateSites] = useState<any>([])

  const generateColor = (): string => {
    let color
    do {
      const randomNum: number = Math.floor(Math.random() * 16777215)
      color = randomNum.toString(16)
    } while (color.startsWith('f') || color.startsWith('e') || color.length < 6)
    return color
  }

  const sendEvent = (event: string): void => {
    Mixpanel.track(event, {
      page: 'Dashboard',
      section: 'Historical Index Chart',
    })
  }

  useMemo(() => {
    if (sites)
      updateSites(
        sites.map((site: any) => ({
          ...site,
          color: generateColor(),
        })),
      )
  }, [sites])

  useEffect(() => {
    setIsVisible(
      isSitesLoaded
        ? (sites as ProductSite[])?.slice(0, 5).map((site) => site.site_id)
        : [],
    )
  }, [sites])

  // Set All Legends Status to Visible When Page Load
  useEffect(() => {
    const chart = lineRef.current
    chart?.update()
    const legendsLength = chart?.legend?.legendItems?.length
    if (legendsLength) {
      for (let i = 0; i < legendsLength; i++) {
        chart.getDatasetMeta(i).hidden = true
      }
    }
    chart?.update()
    if (legendsLength) {
      for (let i = 0; i < 5; i++) {
        chart.getDatasetMeta(i).hidden = false
      }
    }
    chart?.update()
    if (chart) {
      setChartLegends(
        chart.legend?.legendItems?.map((legendItem) => legendItem),
      )
    }
  }, [isSitesLoaded])

  // Toggle Legend Item for Chart
  const toggleLegend = (index: number) => {
    const chart = lineRef.current
    chart?.update()
    if (chart) {
      if (chart.getDatasetMeta(index).hidden === false) {
        chart.getDatasetMeta(index).hidden = true
        chart.update()
      } else {
        chart.getDatasetMeta(index).hidden = false
        chart.update()
      }
    }
  }

  // Update Checkbox Status Based on the Legend's Current Status
  const updateLegend = (index: number, value: boolean) => {
    setChartLegends((chartLegends) =>
      chartLegends?.map((legend: any) => {
        if (legend?.datasetIndex === index) {
          return { ...legend, hidden: value }
        }
        return legend
      }),
    )
  }

  const setPresetDateFilter = (days: number, unit: string) => {
    setFilter({
      key: `${days}${unit}`,
      startAt: format(
        sub(new Date(), {
          [unit]: days,
        }),
        'yyyy-MM-dd',
      ),
      endAt: format(new Date(), 'yyyy-MM-dd'),
    })
  }

  useEffect(() => {
    if (isSitesLoaded) {
      getChartData({
        siteIds: isVisible.map((id) => id),
        startAt: filter.startAt,
        endAt: filter.endAt,
      })
    }
  }, [sites, isVisible, filter])

  // Create Labels for Chart that Shows the Date
  const labels = currentIndexChartData
    ? currentIndexChartData?.dates.map((item: any) =>
        format(parseISO(item), 'MMM d yyyy, h a'),
      )
    : indexChartData?.dates.map((item: any) =>
        format(parseISO(item), 'MMM d yyyy, h a'),
      )

  const firstData: any = []
  if (currentIndexChartData?.chart) {
    Object.keys(currentIndexChartData?.chart).forEach(function (key, index) {
      const url = key
      const site = setSites.find((s: any) => s.url === url)
      firstData.push({
        label: key,
        data: currentIndexChartData.chart[key],
        fill: `#${site?.color}`,
        borderColor: `#${site?.color}`,
        backgroundColor: `#${site?.color}`,
        tension: 0.3,
        borderWidth: 1.5,
        pointRadius: 0,
      })
    })
  } else if (indexChartData?.chart) {
    Object.keys(indexChartData?.chart).forEach(function (key, index) {
      const url = key
      const site = setSites.find((s: any) => s.url === url)
      firstData.push({
        label: key,
        data: indexChartData.chart[key],
        fill: `#${site?.color}`,
        borderColor: `#${site?.color}`,
        backgroundColor: `#${site?.color}`,
        tension: 0.3,
        borderWidth: 1.5,
        pointRadius: 0,
      })
    })
  }

  // Data for Chart Line
  const d = {
    labels,
    datasets: firstData,
  }

  if (!(indexChartData || currentIndexChartData)) {
    return <LoadingHistoricalIndexChart />
  }
  if (!isChartDataLoading && !isChartDataSuccess) {
    return <NoIndexGraph />
  }
  return (
    <div className="sm:pb-16 pb-4">
      <div className="flex  flex-col sm:col-span-3 sm:border sm:border-solid sm:border-gray-200 sm:rounded-lg sm:shadow">
        <div className="flex flex-col lg:border-b border-solid border-gray-200 container mx-auto py-6 lg:p-6">
          <div className="pb-6 flex flex-col">
            <div className="flex flex-col sm:flex-row sm:justify-between">
              <h1 className="font-medium text-lg text-gray-900 pb-4 sm:pb-0">
                Historical Index Chart
              </h1>
              <div className="pb-5">
                <DateRangePicker
                  onChange={([startAt, endAt]: [Date, Date]) => {
                    setFilter({
                      startAt: format(startAt, 'yyyy-MM-dd'),
                      endAt: format(endAt, 'yyyy-MM-dd'),
                      key: 'custom',
                    })
                  }}
                  filter={filter}
                />
              </div>
            </div>
            <div className="flex flex-row justify-between sm:justify-start border-b sm:border-0 pb-2">
              <TabButton
                value={'7days'}
                currentValue={filter.key}
                onClick={() => {
                  setPresetDateFilter(7, 'days')
                  setPaginations({ first: 0, last: 10 })
                  sendEvent('7 Days Button Clicked')
                }}
              >
                7 days
              </TabButton>
              <TabButton
                value={'30days'}
                currentValue={filter.key}
                onClick={() => {
                  setPresetDateFilter(30, 'days')
                  setPaginations({ first: 0, last: 10 })
                  sendEvent('30 Days Button Clicked')
                }}
              >
                30 days
              </TabButton>
              <TabButton
                value={'2months'}
                currentValue={filter.key}
                onClick={() => {
                  setPresetDateFilter(2, 'months')
                  setPaginations({ first: 0, last: 10 })
                  sendEvent('2 Months Button Clicked')
                }}
              >
                2 Months
              </TabButton>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:space-x-8 space-y-4">
            <div className="w-full">
              {(indexChartData || currentIndexChartData) && (
                <Line
                  className="w-full"
                  ref={lineRef}
                  style={{ width: '100%', height: '100%' }}
                  options={{
                    interaction: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                    plugins: {
                      filler: {
                        propagate: true,
                      },
                      legend: {
                        display: false,
                        position: 'right',
                        labels: {
                          useBorderRadius: true,
                          borderRadius: 5,
                          boxWidth: 10,
                          boxHeight: 10,
                        },
                      },
                      datalabels: {
                        display: false,
                      },
                      title: {
                        display: false,
                      },
                    },
                    scales: {
                      x: {
                        afterFit: function (axis) {
                          axis.ticks.forEach((tick, index) => {
                            if (
                              index === 0 ||
                              index === axis.ticks.length - 1 ||
                              index === Math.floor(axis.ticks.length / 2)
                            ) {
                            } else {
                              axis.ticks[index].label = ''
                            }
                          })
                        },
                        ticks: {
                          callback: function (val, index, ticks) {
                            const label = this.getLabelForValue(val as number)
                            const myLabel = label as string
                            const day = myLabel?.split(',')[0]
                            return day
                          },
                          source: 'auto',
                          display: true,
                          // Disabled rotation for performance
                          maxRotation: 0,
                          //autoSkip: true,
                        },
                      },
                      y: {
                        type: 'linear' as const,
                        display: true,
                        position: 'left' as const,
                      },
                      y1: {
                        type: 'linear' as const,
                        display: false,
                        position: 'right' as const,
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    },
                    spanGaps: true,
                  }}
                  data={d as any}
                />
              )}
            </div>
            <div className="w-full lg:w-1/4 sm:pl-4 flex flex-col justify-between">
              <div className="flex flex-col space-y-2 overflow-x-auto lg:overflow-hidden">
                {sites
                  ? setSites?.map((site: any, index: number) => (
                      <div
                        key={`legend_${index}`}
                        className={` flex-row items-center pr-6 ${
                          index >= paginations.last ? 'hidden' : 'flex'
                        } ${index < paginations.first ? 'hidden' : 'flex'}`}
                      >
                        <ManageTableCheckbox
                          isChecked={
                            isVisible.includes(site?.site_id) ? true : false
                          }
                          handleCheckboxChange={() => {
                            updateLegend(index, !site?.hidden)
                            toggleLegend(index)
                            if (!isVisible.includes(site?.site_id)) {
                              setIsVisible((current) => [
                                ...current,
                                site.site_id,
                              ])
                            } else {
                              const items = isVisible.filter(
                                (item: any) => item !== site.site_id,
                              )
                              setIsVisible(items)
                            }
                          }}
                        />
                        <div
                          className={`w-2.5 h-2.5 rounded-full mt-1 mr-1 bg-${[
                            site.color,
                          ]}`}
                          style={{
                            backgroundColor: `#${site.color}`,
                          }}
                        ></div>
                        <span className="font-normal text-xs text-gray-500 pl-0.5 mt-1">
                          {site?.url}
                        </span>
                      </div>
                    ))
                  : null}
              </div>
              {sites && (
                <div className=" inline-flex justify-between items-center mt-4 pt-2 border-t border-t-gray-200">
                  <button
                    disabled={paginations.first < 9}
                    type="button"
                    onClick={() =>
                      setPaginations({
                        first:
                          sites.length - 10 > 0 ? paginations.first - 10 : 0,
                        last:
                          sites.length - 20 > 0 ? paginations.last - 10 : 10,
                      })
                    }
                    className="p-2 border border-gray-200 rounded-lg"
                  >
                    <LeftArrow
                      stroke={paginations.first < 9 ? '#EAECF0' : '#344054'}
                    />
                  </button>
                  <p className="text-base text-gray-700">
                    {paginations.first + 10 > sites?.length
                      ? sites?.length
                      : paginations.first + 10}
                    /{sites?.length} listed
                  </p>
                  <button
                    disabled={paginations.last > sites?.length}
                    type="button"
                    onClick={() =>
                      setPaginations({
                        first: paginations.first + 10,
                        last: paginations.last + 10,
                      })
                    }
                    className="p-2 border border-gray-200 rounded-lg"
                  >
                    <RightArrow
                      stroke={
                        paginations.last > sites?.length ? '#EAECF0' : '#344054'
                      }
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function LoadingHistoricalIndexChart() {
  return (
    <div className="sm:pb-16 pb-4">
      <div className="flex  flex-col sm:col-span-3 border-0 shadow">
        <div className="flex flex-col border-b border-solid border-gray-200 container mx-auto p-6">
          <div className="flex flex-col">
            <div className="flex flex-col sm:flex-row sm:justify-between">
              <h1 className="font-medium text-lg text-gray-900 pb-4 sm:pb-0">
                Historical Index Chart
              </h1>
              <div className="flex flex-row pb-5">
                <button
                  disabled
                  className="border border-solid border-gray-200 rounded-lg shadow-sm h-10"
                >
                  <span className="px-4 font-medium text-sm text-gray-300">
                    Choose custom dates
                  </span>
                </button>
                <div className="pl-3">
                  <button
                    disabled
                    className="border border-solid border-primary-25 bg-primary-25 rounded-lg shadow-sm h-10"
                  >
                    <span className="px-4 font-medium text-sm text-primary-300">
                      Detailed index view
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col container mx-auto ">
            <div className="flex justify-center items-center">
              <div className="flex flex-col items-center">
                <SpinLoader />
                <div className="font-medium text-sm text-gray-700 pb-1 pt-4">
                  Calculating...
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
